import { ButtonBase, Stack, Typography } from '@mui/material';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import _ from 'lodash';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { Message } from '../campaign-statistics/campaign-statistics-types';
import ChatMessageField from './chat-message-field';
import ChatMessages from './chat-messages';
import { InboxCampaign, InboxContact } from './funnel-inbox.types';

interface Props {
  fullCampaign: InboxCampaign | undefined;
  fullContact: InboxContact | undefined;

  messages: Message[];
  messagesLoading: boolean;
  markAsUnreadLoading: boolean;
  autoSyncLoading: boolean;

  chatMessageFieldInput: string;
  sendMessageLoading: boolean;

  handleChatMessageChange(event: React.ChangeEvent<HTMLInputElement>): void;

  handleSendMessage(): void;

  handleMarkAsUnread(): void;

  handleOpenQualifiedContactPreviewDrawer(linkedInURL: string, profileId?: string, funnelId?: string): () => void;
}

const getInitials = (name: string | undefined | null): string => {
  if (!name || typeof name !== 'string') {
    return '?';
  }
  const trimmedName = name.trim();
  if (!trimmedName) {
    return '?';
  }
  const parts = trimmedName.split(/\s+/);
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  const firstInitial = parts[0].charAt(0).toUpperCase();
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
  return firstInitial + lastInitial;
};

const ContactMessages: React.FC<Props> = ({
  fullCampaign,
  fullContact,
  messages,
  messagesLoading,
  markAsUnreadLoading,
  autoSyncLoading,
  chatMessageFieldInput,
  sendMessageLoading,
  handleChatMessageChange,
  handleSendMessage,
  handleMarkAsUnread,
  handleOpenQualifiedContactPreviewDrawer,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const getFullName = (linkedinAccount?: { _id: string; userEmail: string; firstName?: string; lastName?: string }): string => {
    if (!linkedinAccount) {
      return '-';
    }

    const { firstName, lastName, userEmail } = linkedinAccount;

    if (!firstName && !lastName) {
      return userEmail || '-';
    }

    const fullName = `${firstName || ''} ${lastName || ''}`.trim();
    return fullName || userEmail || '-';
  };

  const senderName = getFullName(fullCampaign?.linkedinAccount);
  const campaignName = fullCampaign?.name || '';
  const receiverName = fullContact?.name || '-';
  const receiveOccupation = fullContact?.occupation || '';

  return (
    <Stack className={classes.contactMessages}>
      <Stack className={classes.header} direction={'row'} alignItems={'center'} gap={2}>
        <div className={classes.contactAvatar}>{getInitials(fullContact?.name)}</div>
        <Stack gap={'2px'} className={classes.headerTexts}>
          <Typography className={classes.contactName}>{receiverName}</Typography>
          <Typography className={classes.contactOccupation}>{receiveOccupation || '-'}</Typography>
        </Stack>
        <Stack className={classes.headerActions} direction={'row'} alignItems={'center'} gap={1}>
          <Typography className={classes.originChip}>
            <Trans i18nKey={'fromLinkedinAccountInCampaign'} values={{ senderName, campaignName }}>
              From <span className={classes.highlight}>{senderName}</span> in <span className={classes.highlight}>{campaignName}</span>
            </Trans>
          </Typography>
          {!_.isNil(fullContact) ? (
            <PaiTooltip title={t('showDetailedContactView')}>
              <ButtonBase
                className={classes.headerButton}
                onClick={handleOpenQualifiedContactPreviewDrawer(fullContact.linkedInURL, fullContact.profileId, fullCampaign?._id)}
              >
                {t('inboxContactDetailView')}
              </ButtonBase>
            </PaiTooltip>
          ) : null}
          {!fullContact?.hasUnreadMessages ? (
            <Stack direction={'row'} alignItems={'center'} gap={1}>
              <ButtonBase className={classes.headerButton} disabled={markAsUnreadLoading} onClick={handleMarkAsUnread}>
                {t('markAsUnread')}
              </ButtonBase>
              {markAsUnreadLoading ? <PaiDotLoader size={'small'} inline={true} /> : null}
            </Stack>
          ) : null}
        </Stack>
      </Stack>

      {messagesLoading ? (
        <Stack className={classes.messagesLoader} direction={'row'} alignItems={'center'} justifyContent={'center'}>
          <PaiDotLoader size={'large'} />
        </Stack>
      ) : (
        <ChatMessages messages={messages} timeZone={fullCampaign?.timeZone} />
      )}

      <ChatMessageField
        chatMessageFieldInput={chatMessageFieldInput}
        senderLinkedinAccount={fullCampaign?.linkedinAccount}
        sendMessageLoading={sendMessageLoading}
        autoSyncLoading={autoSyncLoading}
        handleChatMessageChange={handleChatMessageChange}
        handleSendMessage={handleSendMessage}
      />
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  contactMessages: {
    width: '100%',
    height: '100%',
    borderRadius: '12px',
    background: '#FFF',
    overflow: 'hidden',
  },
  header: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  contactAvatar: {
    flexShrink: 0,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTexts: {
    minWidth: 0,
  },
  contactName: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  contactOccupation: {
    fontSize: theme.typography.pxToRem(14),
    color: theme.palette.greys.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerActions: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  originChip: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    background: theme.palette.backgrounds.main_new,
    borderRadius: '8px',
    padding: '6px 10px',
  },
  highlight: {
    color: theme.palette.primary.main,
  },
  headerButton: {
    height: '34px',
    padding: '0 12px',
    border: `1px solid ${theme.palette.greys.lighter}`,
    background: '#FFF',
    borderRadius: '8px',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.dark,
    whiteSpace: 'nowrap',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    '&.Mui-disabled': {
      color: theme.palette.greys.light,
    },
  },
  messagesLoader: {
    flex: 1,
    minHeight: 0,
    background: theme.palette.backgrounds.main_new,
  },
}));

export default ContactMessages;
